'use client';

import { useEffect } from 'react';
import { create } from 'zustand';

interface MediaPlayingState {
  count: number;
  setCount: (n: number) => void;
}

const useMediaPlayingStore = create<MediaPlayingState>((set) => ({
  count: 0,
  setCount: (n) => set({ count: n }),
}));

/**
 * 현재 페이지에서 <video>/<audio> 재생이 하나라도 활성인지 반환.
 * 투어/코치마크/IdleHint가 재생 중 사용자를 방해하지 않도록 조건으로 사용.
 */
export const useIsMediaPlaying = (): boolean =>
  useMediaPlayingStore((s) => s.count > 0);

/**
 * document 레벨 play/pause/ended 이벤트를 capture phase로 위임하여
 * 페이지 내 모든 <video>/<audio> 재생 상태를 단일 지점에서 추적.
 * ended/pause 이중 fire로 인한 count 오염을 방지하기 위해
 * 재생 중 element Set을 기준으로 size를 계산한다.
 * app root에 1회만 마운트.
 */
export function MediaPlayingTracker() {
  useEffect(() => {
    const playing = new Set<EventTarget>();
    const { setCount } = useMediaPlayingStore.getState();

    const sync = () => setCount(playing.size);

    const onPlay = (e: Event) => {
      if (!e.target) return;
      playing.add(e.target);
      sync();
    };
    const onStop = (e: Event) => {
      if (!e.target) return;
      playing.delete(e.target);
      sync();
    };

    document.addEventListener('play', onPlay, true);
    document.addEventListener('pause', onStop, true);
    document.addEventListener('ended', onStop, true);

    return () => {
      document.removeEventListener('play', onPlay, true);
      document.removeEventListener('pause', onStop, true);
      document.removeEventListener('ended', onStop, true);
      playing.clear();
      setCount(0);
    };
  }, []);

  return null;
}
